import { ExperimentService } from "./shared/services/experiment.service";
import { StickyCtaExperimentService } from "src/app/experiments/sticky-cta/sticky-cta.service";
import {
  findCountryProfile,
  findLanguageProfile,
} from "src/app/countries/index";
import { Component, Renderer2, OnInit, OnDestroy, inject } from "@angular/core";
import { DOCUMENT } from "@angular/common";
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationStart,
  Router,
  Event,
} from "@angular/router";
import { Platform } from "@ionic/angular";
import { register } from "swiper/element/bundle";

import { FirebaseDynamicLinks } from "@pantrist/capacitor-firebase-dynamic-links";
import { TranslateService } from "@ngx-translate/core";

import {
  combineLatest,
  EMPTY,
  from,
  interval,
  Observable,
  of,
  Subscription,
} from "rxjs";
import {
  catchError,
  filter,
  map,
  startWith,
  switchMap,
  take,
  takeWhile,
  tap,
} from "rxjs/operators";

import {
  CHAT_ALLOWED_PATHS,
  CHAT_BUBBLE_OVERLAP_ADJUSTED_PATHS,
  CHAT_BUBBLE_OVERLAP_ADJUSTED_PATHS_XL,
  GTM_EVENT_NAMES,
  RouterStates,
} from "./shared/consts";

import { OnDestroyComponent } from "./shared/components/on-destroy/on-destroy.component";
import { extractReferralCode, isPlatformNative } from "./shared/utils/helpers";

import { AuthService } from "./shared/services/auth.service";
import { LoggerService } from "./shared/services/logger.service";
import { WithdrawalJourneyService } from "./shared/services/withdrawal-journey.service";
import { StorageService } from "./shared/services/storage.service";
import { UserService } from "./shared/services/user.service";
import { environment } from "src/environments/environment";
import { DomainService } from "./shared/services/domain.service";
import { AnalyticsService } from "./shared/services/analytics.service";
import { RemoteConfigService } from "./shared/services/remote-config.service";
import { DateService } from "./shared/services/date.service";
import { CookieConsentService } from "./shared/services/cookie-consent.service";
import {
  CountryCode,
  CountryCodeEnum,
  CountryInfo,
} from "./shared/models/country.model";
import { Environment } from "src/environments/environment.type";
import { ChatType } from "./shared/models/chat.model";
import { ChatService } from "./shared/services/chat.service";
import { PlatformService } from "./shared/services/platform.service";
import { VersionService } from "./shared/services/native-app-version.service";
import { CleverTapService } from "./shared/services/clevertap.service";
import { UserAgentService } from "./shared/services/user-agent.service";
import { BranchLinkService } from "./shared/services/dynamic-link.service";
import { MixpanelService } from "./shared/services/mixpanel.service";
import { LandingRouteService } from "./shared/services/landing-route.service";
import { LandingRouteContext } from "./shared/models/landing.model";
import { hasPersistedFrictionlessOnboardingTreatmentAssignment } from "./shared/models/frictionless-onboarding.model";
import { ZABetaLockCoordinatorService } from "./experiments/ZABetaLock/ZABetaLockCoordinator.service";
import { ZABetaLockExperimentKeyService } from "./experiments/ZABetaLock/ZABetaLock.service";
import { NewVisitorCountryAccessService } from "./shared/services/new-visitor-country-access.service";

import { CtSdkService } from "./shared/services/ct-sdk.service";
import { BranchIdentityService } from "./shared/services/branch-identity.service";

// Swiper's register function to globally register Swiper's custom elements.
// This should only be done once
register();

@Component({
  selector: "app-root",
  templateUrl: "app.component.html",
  styleUrls: ["app.component.scss"],
  standalone: false,
})
export class AppComponent
  extends OnDestroyComponent
  implements OnInit, OnDestroy
{
  /* istanbul ignore next -- Angular DI migration wiring */
  private readonly document = inject<Document>(DOCUMENT);
  /* istanbul ignore next -- Angular DI migration wiring */
  private readonly router = inject(Router);
  /* istanbul ignore next -- Angular DI migration wiring */
  private readonly route = inject(ActivatedRoute);
  private readonly stickyCta = inject(StickyCtaExperimentService);
  private readonly experimentService = inject(ExperimentService);
  /* istanbul ignore next -- Angular DI migration wiring */
  private readonly userService = inject(UserService);
  /* istanbul ignore next -- Angular DI migration wiring */
  private readonly authService = inject(AuthService);
  /* istanbul ignore next -- Angular DI migration wiring */
  private readonly loggerService = inject(LoggerService);
  /* istanbul ignore next -- Angular DI migration wiring */
  private readonly storageService = inject(StorageService);
  /* istanbul ignore next -- Angular DI migration wiring */
  private readonly domainService = inject(DomainService);
  /* istanbul ignore next -- Angular DI migration wiring */
  private readonly analyticsService = inject(AnalyticsService);
  /* istanbul ignore next -- Angular DI migration wiring */
  private readonly platform = inject(Platform);
  /* istanbul ignore next -- Angular DI migration wiring */
  private readonly remoteConfigService = inject(RemoteConfigService);
  /* istanbul ignore next -- Angular DI migration wiring */
  private readonly dateService = inject(DateService);
  /* istanbul ignore next -- Angular DI migration wiring */
  private readonly translateService = inject(TranslateService);
  /* istanbul ignore next -- Angular DI migration wiring */
  private readonly cookieConsentService = inject(CookieConsentService);
  /* istanbul ignore next -- Angular DI migration wiring */
  private readonly renderer = inject(Renderer2);
  /* istanbul ignore next -- Angular DI migration wiring */
  private readonly chatService = inject(ChatService);
  /* istanbul ignore next -- Angular DI migration wiring */
  private readonly nativeAppVersionService = inject(VersionService);
  /* istanbul ignore next -- Angular DI migration wiring */
  private readonly platformService = inject(PlatformService);
  /* istanbul ignore next -- Angular DI migration wiring */
  private readonly cleverTapService = inject(CleverTapService);
  /* istanbul ignore next -- Angular DI migration wiring */
  private readonly agentService = inject(UserAgentService);
  /* istanbul ignore next -- Angular DI migration wiring */
  private readonly dynamicLinkService = inject(BranchLinkService);
  /* istanbul ignore next -- Angular DI migration wiring */
  private readonly mixpanelService = inject(MixpanelService);
  /* istanbul ignore next -- Angular DI migration wiring */
  private readonly landingRouteService = inject(LandingRouteService);
  /* istanbul ignore next -- Angular DI migration wiring */
  private readonly ct = inject(CtSdkService);
  /* istanbul ignore next -- Angular DI migration wiring */
  /* istanbul ignore next -- Angular DI migration wiring */
  private readonly zaBetaLockCoordinator = inject(ZABetaLockCoordinatorService);
  /* istanbul ignore next -- Angular DI migration wiring */
  private readonly zaBetaLockService = inject(ZABetaLockExperimentKeyService);
  /* istanbul ignore next -- Angular DI migration wiring */
  private readonly newVisitorCountryAccess = inject(
    NewVisitorCountryAccessService
  );
  /* istanbul ignore next -- Angular DI migration wiring */
  private readonly branchIdentityService = inject(BranchIdentityService);

  routerStates = RouterStates;

  chatType: ChatType;
  showChat: boolean;
  adjustChatBubblePosition: boolean = false;
  adjustChatBubblePositionXl: boolean = false;
  country: CountryCodeEnum;
  private globalRouterEventsSubscription!: Subscription;
  private zaBetaLockActivationSubscription: Subscription | null = null;

  constructor() {
    super();
    inject(WithdrawalJourneyService).start();

    this.addPreconnect(environment);

    this.platform
      .ready()
      .then(() => {
        return this.initApp();
      })
      .then(() => {
        FirebaseDynamicLinks.addListener("deepLinkOpen", (data) => {
          // handle referral link
          if (data && data.url && data.url.includes("onboarding-intro")) {
            const referralCode = extractReferralCode(data.url);
            if (referralCode) {
              this.storageService.promoCodeSubject.next(referralCode);
              this.storageService.set("promoCode", referralCode);
            }
          }
        });
      })
      .then(() => this.handleNewVersion());

    this.subscriptions.browserBackButton = this.domainService
      .disableBrowserBackButton()
      .subscribe();

    this.subscriptions.hardwareBackButton = this.domainService
      .getHardwareBackButton()
      .subscribeWithPriority(99, () => {});

    this.subscriptions.supportChat = this.userService.userCountryWatch$
      .pipe(
        switchMap((countryInfo: CountryInfo) => {
          if (countryInfo) {
            this.setGTMCountry(countryInfo);
            this.country = countryInfo?.country;

            return combineLatest([
              this.chatService.getChatType$(of(countryInfo.country)),
              this.router.events.pipe(
                filter(
                  (routerEvent) =>
                    routerEvent instanceof NavigationEnd || routerEvent === null
                ),
                startWith(null)
              ),
            ]).pipe(
              map(([chatType]) => {
                this.setChat(countryInfo, chatType);
                return countryInfo;
              })
            );
          }

          return of(countryInfo);
        })
      )
      .subscribe();

    this.startZALiveEnforcementWhenActive();

    this.subscriptions.langSwitch =
      this.translateService.onLangChange.subscribe(() => {
        const currentLang = this.translateService.currentLang;

        const pageLang =
          findLanguageProfile(currentLang)?.pageLanguage ?? currentLang;

        this.document.documentElement.lang = pageLang;
        this.cookieConsentService.changeLanguage(pageLang);
      });

    if (this.platformService.isAndroid()) {
      this.platform.ready().then(() => {
        this.dynamicLinkService.initiateAndHandleDeepLinks();
      });
    }
  }

  ngOnInit(): void {
    this.analyticsService.trackSessionUpdate();
    this.trackAppVisibility();
    this.subscribeToRouterEvents();
    if (this.platformService.isAndroid()) {
      this.subscriptions.pollForCookiePopup = this.pollForElement().subscribe();
    }

    this.globalRouterEventsSubscription = this.router.events.subscribe(
      (event: Event) => {
        if (event instanceof NavigationStart) {
          if (event.url !== "/line-success") {
            const urlParams = new URLSearchParams(window.location.search);
            const loginSuccess = urlParams.get("loginSuccess");
            const userInitiatedBrowserName = urlParams.get("browser");
            const currentBrowser = this.agentService.getBrowser().name;

            if (
              loginSuccess === "true" &&
              userInitiatedBrowserName?.length &&
              currentBrowser?.trim() !== userInitiatedBrowserName?.trim()
            ) {
              this.router.navigate(["/line-success"]);
            }
          }
        }
      }
    );
  }

  pollForElement(): Observable<void> {
    return new Observable<void>((observer) => {
      const polling$ = interval(100).pipe(
        takeWhile(
          () => !document.getElementById("cookiescript_injected_wrapper"),
          true
        ),
        switchMap(() => {
          const element = document.getElementById(
            "cookiescript_injected_wrapper"
          );
          if (element) {
            this.renderer.setStyle(element, "display", "none");
            observer.next();
            observer.complete();
          }
          return [];
        })
      );

      polling$.subscribe({
        complete: () => observer.complete(),
      });

      // stop polling after 2 seconds
      setTimeout(() => {
        observer.complete();
      }, 2000);
    });
  }

  override ngOnDestroy(): void {
    this.subscriptions.stickyCtaConfig?.unsubscribe();
    this.cleverTapService.cleanup();
    if (this.globalRouterEventsSubscription) {
      this.globalRouterEventsSubscription.unsubscribe();
    }
    this.zaBetaLockActivationSubscription?.unsubscribe();
    this.zaBetaLockCoordinator.stopLiveEnforcement();
  }

  private startZALiveEnforcementWhenActive(): void {
    this.zaBetaLockActivationSubscription = this.zaBetaLockService
      .liveValue$()
      .pipe(
        filter((lockActive) => lockActive === true),
        take(1)
      )
      .subscribe(() => this.zaBetaLockCoordinator.startLiveEnforcement());
  }

  private setChat(countryInfo: CountryInfo, chatType?: ChatType): void {
    const currentPath = window.location.pathname;

    this.showChat =
      CHAT_ALLOWED_PATHS.includes(currentPath) ||
      this.isPathAllowedForCountry(countryInfo?.country, currentPath);

    this.chatType =
      chatType ?? this.chatService.getChatType(countryInfo?.country);

    // Adjust position if the content overlaps
    this.adjustChatBubblePosition =
      CHAT_BUBBLE_OVERLAP_ADJUSTED_PATHS.includes(currentPath);

    this.adjustChatBubblePositionXl =
      CHAT_BUBBLE_OVERLAP_ADJUSTED_PATHS_XL.includes(currentPath);
  }

  private isPathAllowedForCountry(
    countryCode: CountryCodeEnum,
    path: string
  ): boolean {
    return (
      findCountryProfile(countryCode)?.chatAllowedPaths.includes(path) ?? false
    );
  }

  doRefresh(event: any) {
    setTimeout(() => {
      event.target.complete();
      window.location.reload();
    }, 2000);
  }

  /**
   * Redirects Firebase default hosting domains to the custom domain.
   * Preview Channel URLs (e.g., ewa-services--pr-123-abc.web.app) are
   * excluded because they don't exactly match the default domains.
   *
   * @returns true if a redirect was initiated (caller should abort init)
   */
  private checkDomain(): boolean {
    if (!environment.enableDomainRedirect) {
      return false;
    }

    const { hostname, pathname, search, hash } = this.getLocation();
    if (!hostname) {
      return false;
    }

    const firebaseDefaultDomains = [
      environment.ewaServices.domain, // e.g. ewa-services.web.app
      `${environment.firebaseConfig.projectId}.firebaseapp.com`,
    ];

    if (!firebaseDefaultDomains.includes(hostname)) {
      return false;
    }

    this.redirectTo(`https://${environment.domain}${pathname}${search}${hash}`);
    return true;
  }

  private getLocation(): Pick<
    Location,
    "hostname" | "pathname" | "search" | "hash"
  > {
    return location;
  }

  private redirectTo(url: string): void {
    window.location.href = url;
  }

  private async initApp(): Promise<void> {
    if (this.checkDomain()) {
      return;
    }

    // Track app launched
    this.mixpanelService.trackAppLaunched();

    /**
     * Resolve landing route context from URL when current route is a landing route.
     */
    const landingRouteContext = this.getLandingRouteContext();

    /**
     * Normalize `/landing` variants to canonical `/landing/:country` early,
     * so downstream initialization (language/country state) uses one URL shape.
     */
    if (landingRouteContext) {
      this.syncLandingRouteToCanonicalPath(landingRouteContext);
    }

    this.cleverTapService.init();
    await this.remoteConfigService.init();

    // Initialize CleverTap basics (no push registration here!)
    await this.ct.notifyDeviceReady();

    this.authService
      .getFireAuthUser()
      .pipe(
        switchMap((firebaseUser) => {
          if (firebaseUser && firebaseUser.uid) {
            // Identify user with Mixpanel
            this.mixpanelService.identify(firebaseUser.uid);

            return this.userService.getUserById().pipe(
              switchMap((userData) => {
                const ionicUserId = userData?.user?.userId;
                if (!ionicUserId) {
                  return of(userData);
                }

                return from(
                  this.branchIdentityService.identify(ionicUserId)
                ).pipe(map(() => userData));
              })
            );
          }

          return of(null);
        }),
        switchMap((userData) => {
          // sync user cache
          if (userData && Object.keys(userData).length) {
            Object.keys(userData).forEach((key) =>
              this.storageService.set(key, userData[key])
            );

            this.loggerService.updateSentryScope(userData.user);
            this.userService.updateUserProfileSubject(userData);

            // Onboarding cohort super props + profile props on app reload.
            const onboardingMixpanelProps: {
              is_open_user: boolean;
              frictionless_onboarding?: boolean;
              frictionless_onboarding_variant?: string;
            } = {
              is_open_user: Boolean(userData.user?.openOnboarding?.enrolled),
            };
            const frictionlessOnboarding =
              userData.user?.frictionlessOnboarding;
            if (
              hasPersistedFrictionlessOnboardingTreatmentAssignment(
                frictionlessOnboarding
              )
            ) {
              onboardingMixpanelProps.frictionless_onboarding = true;
              onboardingMixpanelProps.frictionless_onboarding_variant =
                frictionlessOnboarding.variant;
            }
            this.mixpanelService.register(onboardingMixpanelProps);
            this.mixpanelService.setPeople(onboardingMixpanelProps);

            // Identify first; push registration happens inside identifyUserWithCleverTap()
            this.ct.identifyUserWithCleverTap(userData.user);
          }

          /**
           * Landing routes already resolve country from URL path context,
           * so we skip geo/API country detection and keep the existing flow untouched.
           */
          if (landingRouteContext) {
            return of({
              country: landingRouteContext.country,
              userData: userData || null,
            });
          }

          const getCountry$ =
            userData &&
            userData.user &&
            userData.user.country &&
            Object.values(CountryCodeEnum).includes(userData.user.country)
              ? of({ country: userData.user.country, userData })
              : this.userService.getCountryInfo().pipe(
                  map((countryCode: CountryCode) => ({
                    country: countryCode.code,
                    userData: userData || null,
                  })),
                  catchError(() => {
                    // force opening of the country selection component no matter the error
                    this.userService.handleUnknownCountry();
                    return of(null);
                  })
                );

          return getCountry$;
        }),
        switchMap((result) =>
          result
            ? this.zaBetaLockCoordinator.guardCountryResolution$(result)
            : of(result)
        ),
        switchMap((result) => {
          if (!result) {
            return of(result);
          }

          const { country, userData } = result;
          const language = userData?.user?.language || "";
          const userCountry = userData?.user?.country;

          if (landingRouteContext) {
            return this.applyLandingCountryForSession(
              landingRouteContext,
              userData?.user?.userId
            );
          }

          if (
            userCountry &&
            Object.values(CountryCodeEnum).includes(userCountry) &&
            this.userService.hasBetaAccess(userCountry)
          ) {
            return of(this.userService.setCountry(userCountry, language));
          }

          if (
            country &&
            Object.values(CountryCodeEnum).includes(country) &&
            this.newVisitorCountryAccess.isOpenToNewVisitors(country)
          ) {
            return of(this.userService.setCountry(country, language)).pipe(
              switchMap(() => this.userService.updateLocalUser({ country }))
            );
          }

          const userId = userData?.user?.userId || "";
          return of(this.userService.handleUnknownCountry(userId));
        }),
        take(1)
      )
      .subscribe();

    this.watchRefreshToken();

    // ltoken for lineUID
    this.route.queryParams.subscribe((params) => {
      const ltoken = params["ltoken"];
      if (ltoken) {
        this.authService
          .getFireAuthUser()
          .pipe(
            take(1),
            map((firebaseUser) => !!firebaseUser?.uid),
            switchMap((isLoggedIn) => {
              if (isLoggedIn) {
                return EMPTY;
              }

              return from(this.storageService.set("ltoken", ltoken)).pipe(
                catchError((error) => {
                  this.loggerService.logError(error);
                  return EMPTY;
                })
              );
            })
          )
          .subscribe();
      }
    });

    await this.nativeAppVersionService.loadAppInfo();
    this.updateSentryAppVersionContext();
  }

  private updateSentryAppVersionContext(): void {
    const nativeAppVersion = this.nativeAppVersionService.getAppVersion();
    this.loggerService.updateSentryAppVersionContext({
      appPlatform: this.getAppPlatform(),
      nativeAppVersion,
      nativeBuildNumber: nativeAppVersion
        ? this.nativeAppVersionService.versionCode
        : null,
    });
  }

  private getAppPlatform(): string {
    if (this.platformService.isAndroidApp()) {
      return "android";
    }

    if (this.platformService.isIosApp()) {
      return "ios";
    }

    if (this.platformService.isNative()) {
      return "native";
    }

    return "web";
  }

  /**
   * Returns landing route context when current route matches the landing route shape.
   *
   * @returns {LandingRouteContext | null}
   * A parsed landing route context when current route is a landing route;
   * otherwise `null`.
   */
  private getLandingRouteContext(): LandingRouteContext | null {
    return this.landingRouteService.getRouteContext();
  }

  /**
   * Redirects non-canonical landing URLs to canonical `/landing/:country`.
   * Ensures a single URL shape for routing and country resolution.
   *
   * @param {LandingRouteContext} landingRouteContext
   * Parsed landing route context containing canonical path and canonical-match state.
   * @returns {void}
   */
  private syncLandingRouteToCanonicalPath(
    landingRouteContext: LandingRouteContext
  ): void {
    if (landingRouteContext.isCanonicalPath) {
      return;
    }

    this.router.navigate([landingRouteContext.canonicalPath], {
      queryParamsHandling: "merge",
      replaceUrl: true,
    });
  }

  /**
   * Applies URL-derived landing country without opening the country selection modal.
   *
   * For authenticated users, the country is not applied at the app level to avoid
   * overriding the profile-selected country and language.
   * For anonymous users, the country is also persisted locally to keep subsequent flows
   * consistent after landing entry.
   *
   * @param landingRouteContext Parsed landing route context.
   * @param userId Authenticated user id (if available).
   * @returns Observable of the applied operation.
   */
  private applyLandingCountryForSession(
    landingRouteContext: LandingRouteContext,
    userId?: string
  ): Observable<unknown> {
    const landingCountry = landingRouteContext.country;

    if (userId) {
      return of(true);
    }

    return of(this.userService.setCountry(landingCountry)).pipe(
      switchMap(() =>
        this.userService.updateLocalUser({
          country: landingCountry,
        })
      )
    );
  }

  private watchRefreshToken() {
    // check token initially
    this.refreshToken().pipe(take(1)).subscribe();

    const tenMinutesInMs = 10 * 60 * 1000;
    this.subscriptions.watchUserAuthState = interval(tenMinutesInMs)
      .pipe(switchMap(() => this.refreshToken()))
      .subscribe();
  }

  private refreshToken(): Observable<any> {
    const idTokenResult$ = this.authService.getIdTokenResult();
    const firebaseUser$ = this.authService.getFireAuthUser();

    return idTokenResult$.pipe(
      switchMap((tokenResult) => {
        return firebaseUser$.pipe(
          map((firebaseUser) => ({ tokenResult, firebaseUser }))
        );
      }),
      switchMap(({ tokenResult, firebaseUser }) => {
        if (!firebaseUser || !tokenResult) {
          return of(null);
        }

        const fiveMinutesInMs = 5 * 60 * 1000;
        const currentTime = new Date().getTime();
        const expireTime = new Date(tokenResult.expirationTime).getTime();
        const refresh =
          expireTime && expireTime - fiveMinutesInMs < currentTime;

        // convert to ms
        const authTime = this.dateService.convertSecondsToMilliseconds(
          tokenResult.claims.auth_time
        );
        const iat = this.dateService.convertSecondsToMilliseconds(
          tokenResult.claims.iat
        );

        const activeDays = this.dateService.getDifferenceInDays(iat, authTime);
        if (activeDays && activeDays >= 30) {
          return this.authService
            .logout()
            .pipe(tap(() => this.router.navigate(["/login"])));
        }

        /** Returns the current token if it has not expired. Otherwise, this will
         * refresh the token and return a new one */
        return firebaseUser.getIdToken(refresh);
      }),
      catchError((error) => {
        this.loggerService.logError(error);
        return of(null);
      })
    );
  }

  private async handleNewVersion() {
    this.nativeAppVersionService.displayUpdatePrompt();
  }

  private setGTMCountry(countryInfo: CountryInfo): void {
    window.gtm_country_code = countryInfo?.country ?? "";
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: GTM_EVENT_NAMES.countryCodeReady,
    });
  }

  openChat(): void {
    this.chatService.openContactChannel(true).pipe(take(1)).subscribe();
  }

  /**
   * Sets up visibility tracking for the app, detecting when the app moves to and from the background
   * on both native and web platforms...
   *
   * @returns {void}
   */
  private trackAppVisibility() {
    this.subscriptions.stickyCtaConfig = this.experimentService
      .sdkEvaluationChanges$()
      .subscribe(() => void this.stickyCta.refreshSession());
    if (isPlatformNative()) {
      this.subscriptions.resume = this.platform.resume
        .pipe(
          // always run these side-effects on resume
          tap(() => {
            this.analyticsService.trackSessionUpdate();
            void this.stickyCta.refreshSession();
          }),
          // only proceed to permission/registration on Android and if not yet registered.
          filter(
            () => this.platformService.isAndroid() && !this.ct.pushRegistered
          ),
          // Promise<boolean> -> Observable<boolean>
          switchMap(() => from(this.ct.ensurePushPermission())),
          // continue only if permission is granted
          filter((granted) => !!granted),
          // Promise<void> -> Observable<void>
          switchMap(() => from(this.ct.registerPushWithCleverTap()))
        )
        .subscribe();

      return;
    }

    // web
    this.document.addEventListener("visibilitychange", () => {
      if (this.document.visibilityState === "visible") {
        this.analyticsService.trackSessionUpdate();
        void this.stickyCta.refreshSession();
      }
    });
  }

  private addPreconnect(env: Environment) {
    const addLink = (domain: string, crossorigin: boolean = false) => {
      if (domain) {
        const link = this.renderer.createElement("link");
        this.renderer.setAttribute(link, "rel", "preconnect");
        this.renderer.setAttribute(link, "href", domain);
        if (crossorigin) {
          this.renderer.setAttribute(link, "crossorigin", "anonymous");
        }

        const head = document.head;
        const firstChild = head.firstChild;
        if (firstChild) {
          this.renderer.insertBefore(head, link, firstChild);
        } else {
          this.renderer.appendChild(head, link);
        }
      }
    };

    env.preloadableDomains.forEach((domain) => addLink(domain));
    [env.requestUrl, env.firebaseConfig.databaseURL].forEach((domain) =>
      addLink(domain, true)
    );
  }

  /**
   * Subscribes to router navigation events to perform various tracking tasks.
   */
  private subscribeToRouterEvents(): void {
    this.subscriptions.routerEvents = this.router.events
      .pipe(
        // Only process completed navigation events
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),

        // Save employer code
        tap(() => this.saveEmployerCode()),

        // Track user state in localStorage for GTM/CleverTap
        switchMap(() => this.analyticsService.trackUserStateForGtm())
      )
      .subscribe();
  }

  /**
   * Extracts the employer code query parameter from the current route and saves it
   * to storage using the StorageService for later use
   */
  private saveEmployerCode(): void {
    const ecParam = this.route.snapshot.queryParamMap.get("ec");

    if (ecParam) {
      this.storageService.set("employerCode", ecParam);
    }
  }
}
